import { Container, ContainerSize, Heading, Pagination, PaginationAlignment, PaginationSize, Text, Wrapper } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";
type PaginationProps = {}

const PaginationPage = ({ }: PaginationProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Pagination
            </Heading>

            <Text el="p"><Text el="strong">Pagination</Text> is used to divide large amounts of content or data into
                smaller, manageable pages —
                allowing users to navigate between them easily.</Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Usage
            </Heading>
            <Text className="mb-4">
                In this component, <Text el="strong">pagination</Text> can be implemented using the <Text
                    el="strong">Pagination</Text> element, which provides navigation
                controls like Previous, Next, and numbered <Text el="strong">page links</Text> to move through data sets
                or lists efficiently.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Pagination size={PaginationSize.Sm} className='pagination-lg'>
                        <Pagination.Item to="#">Previous</Pagination.Item>
                        <Pagination.Item to="#">1</Pagination.Item>
                        <Pagination.Item to="#">2</Pagination.Item>
                        <Pagination.Item to="#">3</Pagination.Item>
                        <Pagination.Item to="#">Next</Pagination.Item>
                    </Pagination>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {paginationBasicCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Pagination with icons
            </Heading>
            <Text className="mb-4">
                If you prefer using <Text el="strong">icons</Text> or <Text el="strong">symbols</Text> instead of text
                in your <Text el="strong">pagination</Text>, ensure that each icon is accessible by adding suitable
                labels.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Pagination>
                        <Pagination.Item to="#">&laquo;</Pagination.Item>
                        <Pagination.Item to="#">1</Pagination.Item>
                        <Pagination.Item to="#">2</Pagination.Item>
                        <Pagination.Item to="#">3</Pagination.Item>
                        <Pagination.Item to="#">&raquo;</Pagination.Item>
                    </Pagination>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {paginationIconCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Active and disabled link
            </Heading>
            <Wrapper className="mb-4">
                <Text className="mb-1"> You can indicate the <Text el="strong">current page</Text> and <Text el="strong">inactive navigation options</Text> using the <Text el="strong">isActive</Text> and <Text el="strong">isDisabled props</Text>. </Text>
                <Text className="mb-1"> <Text el="strong">isActive</Text> sets to <Text el="strong">true</Text> highlights the current page number. </Text>
                <Text className="mb-1"> <Text el="strong">isDisabled</Text> sets to <Text el="strong">true</Text> visually disables a link and prevents user interaction. </Text>
            </Wrapper>
            <CodeSnippet>
                <CodePreview>
                    <Pagination>
                        <Pagination.Item to="#" isDisabled={true}>Previous</Pagination.Item>
                        <Pagination.Item to="#">1</Pagination.Item>
                        <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
                        <Pagination.Item to="#">3</Pagination.Item>
                        <Pagination.Item to="#">Next</Pagination.Item>
                    </Pagination>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {paginationActiveDisabledCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Sizing
            </Heading>
            <Wrapper className="mb-4">
                <Text className="mb-1"> You can adjust the overall <Text el="strong">size</Text> of the pagination using the <Text el="strong">size prop</Text>. </Text>
                <Text className="mb-1"> <Text el="strong">PaginationSize.Lg</Text> Displays a larger version of the pagination for better visibility. </Text>
                <Text className="mb-1"> <Text el="strong">PaginationSize.Sm</Text> Displays a smaller, more compact version suitable for tight layouts. </Text>
            </Wrapper>

            <CodeSnippet>
                <CodePreview>
                    <Pagination size={PaginationSize.Lg}>
                        <Pagination.Item to="#">Previous</Pagination.Item>
                        <Pagination.Item to="#">1</Pagination.Item>
                        <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
                        <Pagination.Item to="#">3</Pagination.Item>
                        <Pagination.Item to="#">Next</Pagination.Item>
                    </Pagination>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {paginationLargeCode}
                </CodeHighlighter>
            </CodeSnippet>
            <CodeSnippet>
                <CodePreview>
                    <Pagination size={PaginationSize.Sm}>
                        <Pagination.Item to="#">Previous</Pagination.Item>
                        <Pagination.Item to="#">1</Pagination.Item>
                        <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
                        <Pagination.Item to="#">3</Pagination.Item>
                        <Pagination.Item to="#">Next</Pagination.Item>
                    </Pagination>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {paginationSmallCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Alignment
            </Heading>
            <Wrapper className="mb-4">
                <Text className="mb-1"> You can control the horizontal alignment of the pagination using the alignment prop. </Text>
                <Text className="mb-1"> <Text el="strong">PaginationAlignment.Start</Text> Aligns the pagination to the left (default) </Text>
                <Text className="mb-1"> <Text el="strong">PaginationAlignment.Center</Text> Centers the pagination within its container. </Text>
                <Text className="mb-1"> <Text el="strong">PaginationAlignment.End</Text> Aligns the pagination to the right side. </Text>
            </Wrapper>

            <CodeSnippet>
                <CodePreview>
                    <Pagination alignment={PaginationAlignment.Start}>
                        <Pagination.Item to="#" isDisabled={true}>Previous</Pagination.Item>
                        <Pagination.Item to="#">1</Pagination.Item>
                        <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
                        <Pagination.Item to="#">3</Pagination.Item>
                        <Pagination.Item to="#">Next</Pagination.Item>
                    </Pagination>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {paginationStartCode}
                </CodeHighlighter>
            </CodeSnippet>
            <CodeSnippet>
                <CodePreview>
                    <Pagination alignment={PaginationAlignment.End}>
                        <Pagination.Item to="#" isDisabled={true}>Previous</Pagination.Item>
                        <Pagination.Item to="#">1</Pagination.Item>
                        <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
                        <Pagination.Item to="#">3</Pagination.Item>
                        <Pagination.Item to="#">Next</Pagination.Item>
                    </Pagination>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {paginationEndCode}
                </CodeHighlighter>
            </CodeSnippet>
            <CodeSnippet>
                <CodePreview>
                    <Pagination alignment={PaginationAlignment.Center}>
                        <Pagination.Item to="#" isDisabled={true}>Previous</Pagination.Item>
                        <Pagination.Item to="#">1</Pagination.Item>
                        <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
                        <Pagination.Item to="#">3</Pagination.Item>
                        <Pagination.Item to="#">Next</Pagination.Item>
                    </Pagination>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {paginationCenterCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    )
}

export default PaginationPage;

const paginationBasicCode = `<Pagination>
    <Pagination.Item to="#">Previous</Pagination.Item>
    <Pagination.Item to="#">1</Pagination.Item>
    <Pagination.Item to="#">2</Pagination.Item>
    <Pagination.Item to="#">3</Pagination.Item>
    <Pagination.Item to="#">Next</Pagination.Item>
</Pagination>`;

const paginationIconCode = `<Pagination>
    <Pagination.Item to="#">&laquo;</Pagination.Item>
    <Pagination.Item to="#">1</Pagination.Item>
    <Pagination.Item to="#">2</Pagination.Item>
    <Pagination.Item to="#">3</Pagination.Item>
    <Pagination.Item to="#">&raquo;</Pagination.Item>
</Pagination>`;

const paginationActiveDisabledCode = `<Pagination>
    <Pagination.Item to="#" isDisabled={true}>Previous</Pagination.Item>
    <Pagination.Item to="#">1</Pagination.Item>
    <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
    <Pagination.Item to="#">3</Pagination.Item>
    <Pagination.Item to="#">Next</Pagination.Item>
</Pagination>`;

const paginationLargeCode = `<Pagination size={PaginationSize.Lg}>
    <Pagination.Item to="#">Previous</Pagination.Item>
    <Pagination.Item to="#">1</Pagination.Item>
    <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
    <Pagination.Item to="#">3</Pagination.Item>
    <Pagination.Item to="#">Next</Pagination.Item>
</Pagination>`;

const paginationSmallCode = `<Pagination size={PaginationSize.Sm}>
    <Pagination.Item to="#">Previous</Pagination.Item>
    <Pagination.Item to="#">1</Pagination.Item>
    <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
    <Pagination.Item to="#">3</Pagination.Item>
    <Pagination.Item to="#">Next</Pagination.Item>
</Pagination>`;

const paginationEndCode = `<Pagination alignment={PaginationAlignment.End}>
    <Pagination.Item to="#" isDisabled={true}>Previous</Pagination.Item>
    <Pagination.Item to="#">1</Pagination.Item>
    <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
    <Pagination.Item to="#">3</Pagination.Item>
    <Pagination.Item to="#">Next</Pagination.Item>
</Pagination>`;

const paginationCenterCode = `<Pagination alignment={PaginationAlignment.Center}>
    <Pagination.Item to="#" isDisabled={true}>Previous</Pagination.Item>
    <Pagination.Item to="#">1</Pagination.Item>
    <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
    <Pagination.Item to="#">3</Pagination.Item>
    <Pagination.Item to="#">Next</Pagination.Item>
</Pagination>`;

const paginationStartCode = `<Pagination alignment={PaginationAlignment.Start}>
    <Pagination.Item to="#" isDisabled={true}>Previous</Pagination.Item>
    <Pagination.Item to="#">1</Pagination.Item>
    <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
    <Pagination.Item to="#">3</Pagination.Item>
    <Pagination.Item to="#">Next</Pagination.Item>
</Pagination>`;

